import React, { useState } from 'react';
import { EnvelopeIcon, PhoneIcon, MapPinIcon } from '@heroicons/react/24/outline';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    subscribeToNewsletter: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // In a real app, you would send the form data to your API
      // const response = await api.post('/api/contact', formData);
      
      setSubmitted(true);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        message: '',
        subscribeToNewsletter: false
      });
    } catch (err) {
      setError('There was an error submitting your form. Please try again.');
      console.error('Contact form submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="text-white py-24 sm:py-32 overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        {/* Green gradient circle in top right */}
        <div className="absolute w-[500px] h-[500px] top-[-100px] right-[-100px] opacity-30 bg-gradient-radial from-[#1ABC9D] to-transparent rounded-full"></div>
        
        {/* Purple small circle mid-left */}
        <div className="absolute w-[80px] h-[80px] top-[400px] left-[100px] opacity-40 bg-gradient-radial from-[#ABA2FE] to-transparent rounded-full"></div>
        
        {/* Purple medium circle bottom-left */}
        <div className="absolute w-[150px] h-[150px] bottom-[100px] left-[200px] opacity-30 bg-gradient-radial from-[#ABA2FE] to-transparent rounded-full"></div>
        
        {/* Orange glow top-right */}
        <div className="absolute w-[300px] h-[200px] top-[200px] right-[-50px] opacity-20 bg-gradient-radial from-[#DE7A44] to-transparent rounded-full"></div>
      </div>

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        {/* Header section */}
        <div className="mx-auto max-w-3xl text-center mb-16">
          <h2 className="text-center text-[#1ABC9D] text-xl font-bold tracking-wider mb-2">BUILT WITH PRIDE IN NOVA SCOTIA CANADA</h2>
          <h3 className="text-center text-[#ABA2FE] text-base font-bold tracking-wider mt-8">CONTACT US</h3>
          <p className="mt-2 text-4xl font-semibold tracking-tight text-white sm:text-5xl">
            Get in touch with our team
          </p>
          <p className="mt-6 text-lg leading-8 text-[#D4D2ED]">
            Have questions about RocketScanner? Our team is here to help.
          </p>
        </div>

        {/* Two column layout for contact information and form */}
        <div className="mx-auto max-w-7xl lg:grid lg:grid-cols-2 lg:gap-x-12">
          {/* Left column - Contact information */}
          <div className="space-y-8 lg:pr-8">
            <div className="bg-[#101729] p-6 rounded-xl shadow-lg border border-gray-800">
              <h3 className="text-base font-semibold leading-7 text-white border-l-2 border-[#1ABC9D] pl-4">Our Office</h3>
              <address className="mt-3 space-y-1 text-sm not-italic text-[#D4D2ED] border-l-2 border-gray-700 pl-4 pt-2">
                <div className="flex items-center">
                  <MapPinIcon className="h-5 w-5 flex-shrink-0 text-[#1ABC9D] mr-2" aria-hidden="true" />
                  <div>
                    <p>848 Main Street</p>
                    <p>Dartmouth, Nova Scotia</p>
                    <p>Canada</p>
                  </div>
                </div>
              </address>
            </div>
            
            <div className="bg-[#101729] p-6 rounded-xl shadow-lg border border-gray-800">
              <h3 className="text-base font-semibold leading-7 text-white border-l-2 border-[#1ABC9D] pl-4">Contact Details</h3>
              <dl className="mt-3 space-y-4 text-sm leading-6 text-[#D4D2ED] border-l-2 border-gray-700 pl-4 pt-2">
                <div>
                  <dt className="font-medium text-white mb-1">Email</dt>
                  <dd className="flex items-center">
                    <EnvelopeIcon className="h-5 w-5 flex-shrink-0 text-[#1ABC9D] mr-2" aria-hidden="true" />
                    <span>sales@rocketscanner.com</span>
                  </dd>
                  <dd className="flex items-center mt-2">
                    <EnvelopeIcon className="h-5 w-5 flex-shrink-0 text-[#1ABC9D] mr-2" aria-hidden="true" />
                    <span>support@rocketscanner.com</span>
                  </dd>
                </div>
                <div>
                  <dt className="font-medium text-white mb-1">Phone</dt>
                  <dd className="flex items-center">
                    <PhoneIcon className="h-5 w-5 flex-shrink-0 text-[#1ABC9D] mr-2" aria-hidden="true" />
                    <span>+1 (800) 123-4567</span>
                  </dd>
                  <dd className="flex items-center mt-2">
                    <PhoneIcon className="h-5 w-5 flex-shrink-0 text-[#1ABC9D] mr-2" aria-hidden="true" />
                    <span>+1 (800) 765-4321</span>
                  </dd>
                </div>
              </dl>
            </div>
            
            <div className="bg-[#101729] p-6 rounded-xl shadow-lg border border-gray-800">
              <h3 className="text-base font-semibold leading-7 text-white border-l-2 border-[#1ABC9D] pl-4">Connect with us</h3>
              <div className="mt-4 flex space-x-6 border-l-2 border-gray-700 pl-4 pt-2">
                <a href="#" className="text-[#ABA2FE] hover:text-white">
                  <span className="sr-only">Twitter</span>
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                  </svg>
                </a>
                <a href="#" className="text-[#ABA2FE] hover:text-white">
                  <span className="sr-only">LinkedIn</span>
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path fillRule="evenodd" d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" clipRule="evenodd" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
          
          {/* Right column - Contact form */}
          <div className="mt-12 lg:mt-0">
            <div className="bg-[#101729] rounded-xl p-8 shadow-lg border border-gray-800">
              <h3 className="text-lg font-semibold text-white mb-6 border-l-2 border-[#1ABC9D] pl-4">Send us a message</h3>
              
              {submitted ? (
                <div className="rounded-md bg-[#1ABC9D]/20 p-6">
                  <div className="flex">
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-[#1ABC9D]">Message sent successfully</h3>
                      <div className="mt-2 text-sm text-white">
                        <p>Thank you for reaching out! Our team will get back to you shortly.</p>
                      </div>
                      <div className="mt-4">
                        <button
                          type="button"
                          onClick={() => setSubmitted(false)}
                          className="rounded-full bg-[#1ABC9D] px-4 py-2 text-sm font-medium text-[#0B0F19] hover:bg-[#15A589] focus:outline-none shadow-md border-t border-[#D6F0EB]"
                        >
                          Send another message
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
                  {error && (
                    <div className="sm:col-span-2">
                      <div className="rounded-md bg-red-900/20 p-4">
                        <div className="flex">
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-400">{error}</h3>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-semibold leading-6 text-white">
                      First name
                    </label>
                    <div className="mt-2">
                      <input
                        type="text"
                        name="firstName"
                        id="firstName"
                        autoComplete="given-name"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        className="block w-full rounded-md border-0 px-3.5 py-2 bg-[#101729] text-white shadow-sm ring-1 ring-inset ring-[#19BC9C] placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-[#19BC9C] sm:text-sm sm:leading-6"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-semibold leading-6 text-white">
                      Last name
                    </label>
                    <div className="mt-2">
                      <input
                        type="text"
                        name="lastName"
                        id="lastName"
                        autoComplete="family-name"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        className="block w-full rounded-md border-0 px-3.5 py-2 bg-[#101729] text-white shadow-sm ring-1 ring-inset ring-[#19BC9C] placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-[#19BC9C] sm:text-sm sm:leading-6"
                      />
                    </div>
                  </div>
                  
                  <div className="sm:col-span-2">
                    <label htmlFor="company" className="block text-sm font-semibold leading-6 text-white">
                      Company
                    </label>
                    <div className="mt-2">
                      <input
                        type="text"
                        name="company"
                        id="company"
                        autoComplete="organization"
                        value={formData.company}
                        onChange={handleChange}
                        className="block w-full rounded-md border-0 px-3.5 py-2 bg-[#101729] text-white shadow-sm ring-1 ring-inset ring-[#19BC9C] placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-[#19BC9C] sm:text-sm sm:leading-6"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold leading-6 text-white">
                      Email
                    </label>
                    <div className="mt-2">
                      <input
                        type="email"
                        name="email"
                        id="email"
                        autoComplete="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="block w-full rounded-md border-0 px-3.5 py-2 bg-[#101729] text-white shadow-sm ring-1 ring-inset ring-[#19BC9C] placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-[#19BC9C] sm:text-sm sm:leading-6"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold leading-6 text-white">
                      Phone number
                    </label>
                    <div className="mt-2">
                      <input
                        type="tel"
                        name="phone"
                        id="phone"
                        autoComplete="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        className="block w-full rounded-md border-0 px-3.5 py-2 bg-[#101729] text-white shadow-sm ring-1 ring-inset ring-[#19BC9C] placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-[#19BC9C] sm:text-sm sm:leading-6"
                      />
                    </div>
                  </div>
                  
                  <div className="sm:col-span-2">
                    <label htmlFor="message" className="block text-sm font-semibold leading-6 text-white">
                      Message
                    </label>
                    <div className="mt-2">
                      <textarea
                        name="message"
                        id="message"
                        rows={4}
                        value={formData.message}
                        onChange={handleChange}
                        required
                        className="block w-full rounded-md border-0 px-3.5 py-2 bg-[#101729] text-white shadow-sm ring-1 ring-inset ring-[#19BC9C] placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-[#19BC9C] sm:text-sm sm:leading-6"
                      />
                    </div>
                  </div>
                  
                  <div className="flex gap-x-4 sm:col-span-2">
                    <div className="flex h-6 items-center">
                      <input
                        id="subscribeToNewsletter"
                        name="subscribeToNewsletter"
                        type="checkbox"
                        checked={formData.subscribeToNewsletter}
                        onChange={handleChange}
                        className="h-4 w-4 rounded border-gray-700 bg-[#101729] text-[#19BC9C] focus:ring-[#19BC9C]"
                      />
                    </div>
                    <label htmlFor="subscribeToNewsletter" className="text-sm leading-6 text-[#D4D2ED]">
                      By selecting this, you agree to receive product and feature updates via email.
                    </label>
                  </div>
                  
                  <div className="sm:col-span-2 mt-4">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="block w-full rounded-full bg-[#1ABC9D] hover:bg-[#15A589] px-3.5 py-3 text-center text-sm font-bold text-[#0B0F19] shadow-md border-t border-[#D6F0EB] disabled:bg-[#1ABC9D]/50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? 'Sending...' : 'Send message'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
